// api/client.mjs — fetch wrapper for the dev_coordinator backend.
//
// Contract (matches the C++ gateway, product_src/backend/src/gateway/server.cpp):
//   * Same-origin. In production the backend hosts the SPA and the API under one
//     origin; in dev, build.mjs proxies /api/* to the dev backend.
//   * credentials:'include' on every call — auth is HttpOnly cookies
//     (dc_session / dc_device). The frontend NEVER reads or sets these cookies.
//   * JSON in / JSON out.
//   * Errors share one shape: { "error": { "code", "message", ... } }.
//     429 carries retryAfterSeconds (and a Retry-After header).
//     401 is surfaced to the caller (AuthContext treats it as "signed out").

// ── SB-134：本页启动时的那枚戳 ─────────────────────────────────────────────
// 第一次见到的戳即为「这一页拿到的那一版」：页面刚加载完就会打第一条 API，
// 那一刻服务端给的按定义就是它。自校准，且少一处构建期与运行期可能对不上的地方。
let bootRev = null;
let serverRev = null;
const revListeners = new Set();

export function noteServerRev(rev) {
  if (!rev) return;
  serverRev = rev;
  if (bootRev === null) bootRev = rev;
  for (const fn of revListeners) fn({ bootRev, serverRev });
}
export function currentRevs() { return { bootRev, serverRev }; }
export function onRevChange(fn) {
  revListeners.add(fn);
  return () => revListeners.delete(fn);
}
// 判据用：把模块级状态清回初值（模块单例在测试之间会串味）。
export function resetRevsForTest() { bootRev = null; serverRev = null; revListeners.clear(); }

// ── 第一百八十三批：dc-cli 的「当前版 / 最低支持版」随每条应答同行 ─────────────
// 服务端在**所有** /api/* 应答上盖 `X-DC-CLI-Current-Version`（取 cli 清单；清单缺席
// 时省略）与 `X-DC-CLI-Min-Version`（配置 cli.minSupportedVersion）。与 X-Build-Rev
// 同一条纪律：不为它新开一条请求——管理页要判「这把 key 的客户端落后了没」时，
// 页面自己的加载流量里已经把答案带回来了。读取放在这里而不是页面里，是因为
// 页面只见得到 JSON 体，响应头在 request() 之外没有第二个观察点。
// 只记**形状合法**的值（三段整数）：一个被代理层改坏的头进了比较函数，结果会是
// 「所有 key 都需升级」——那比没有徽记更糟。
const VERSION_SHAPE = /^\d+\.\d+\.\d+$/;
let cliCurrent = null;
let cliMin = null;
const cliListeners = new Set();

export function noteCliVersions(current, min) {
  let changed = false;
  if (typeof current === 'string' && VERSION_SHAPE.test(current) && current !== cliCurrent) {
    cliCurrent = current; changed = true;
  }
  if (typeof min === 'string' && VERSION_SHAPE.test(min) && min !== cliMin) {
    cliMin = min; changed = true;
  }
  if (!changed) return;
  for (const fn of cliListeners) fn({ current: cliCurrent, min: cliMin });
}
export function cliVersionHints() { return { current: cliCurrent, min: cliMin }; }
export function onCliVersionHints(fn) {
  cliListeners.add(fn);
  return () => cliListeners.delete(fn);
}
export function resetCliVersionHintsForTest() {
  cliCurrent = null; cliMin = null; cliListeners.clear();
}

export class ApiError extends Error {
  constructor(code, message, status, extra = {}) {
    super(message || code || 'request failed');
    this.name = 'ApiError';
    this.code = code || `HTTP_${status}`;
    this.status = status;
    this.retryAfterSeconds = extra.retryAfterSeconds;
    this.extra = extra;
  }
}

// ── 读者的语言，随每一个请求同行（SB-162，第一百三十一批需求方实撞）─────────
// 病灶：服务端要投影一段**存着的**文本（工作项标题、文档标题）时，得知道读者是谁的
// 语言；此前这靠**每个调用点记得传 `?lang=`**，于是站内信那条链上四处忘了传，外包方
// 在英文界面上看到中文的工作项标题。**一条只在自己人身上被验证过的规则**：zh 优先对
// 内部读者恰好总是对的，所以它从没被发现是错的。
//
// 结构修：语言在**公共请求层**取一次、随每个请求同行。`<html lang>` 是权威——
// i18n 的 Provider 自挂载起就把活动语言写在那里（返回的 en 用户以前会保留 zh 直到
// 手动切一次，那条已在第四十三批修掉），故这里读它而不是再穿一遍 React 上下文：
// 请求层不该为了一个字符串而依赖组件树。
// **显式 `?lang=` 仍然优先**——文档页的语言切换器要的就是"看另一种语言"，那与
// 界面语言是两件事。
function viewerLang() {
  try {
    const l = document.documentElement.getAttribute('lang');
    return l === 'en' || l === 'zh' ? l : null;
  } catch { return null; }   // 非浏览器环境（单测/SSR）：不带头，服务端走默认
}

async function request(method, path, body) {
  const opts = { method, credentials: 'include', headers: {} };
  const lang = viewerLang();
  if (lang) opts.headers['X-DC-Lang'] = lang;
  if (body !== undefined) {
    opts.headers['Content-Type'] = 'application/json';
    opts.headers['Accept'] = 'application/json';
    opts.body = JSON.stringify(body);
  }

  let res;
  try {
    res = await fetch(path, opts);
  } catch (e) {
    // Network / CORS / abort — no HTTP status available.
    throw new ApiError('NETWORK', e?.message || 'network error', 0);
  }

  // ── SB-134（第一百一十四批）：构建戳搭已经在跑的这趟车 ────────────────────
  // 后端给每条 /api/* 应答盖 `X-Build-Rev`。这里是**唯一**读它的地方——
  // 前端不新开轮询：通知铃每 60 秒本来就要拉一次，加一条自己的轮询就是加一条
  // 会漂、会失败、会被人关掉的东西，而答案在既有流量里已经有了。
  // **成功与失败的应答都读**：401/429 一样带头，而一个只在成功时更新版本的
  // 观察者，恰好在服务端刚换版、旧前端开始撞契约的那一刻停止观察。
  noteServerRev(res.headers.get('X-Build-Rev'));
  // 第一百八十三批：同一趟车上的另外两枚头（见 noteCliVersions 头注）。
  noteCliVersions(res.headers.get('X-DC-CLI-Current-Version'),
                  res.headers.get('X-DC-CLI-Min-Version'));

  // Parse JSON body when present; tolerate empty/non-JSON responses.
  let data = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }

  if (!res.ok) {
    const err = data && typeof data === 'object' && data.error ? data.error : {};
    const headerRetry = res.headers.get('Retry-After');
    const retryAfterSeconds =
      err.retryAfterSeconds ?? (headerRetry != null ? Number(headerRetry) : undefined);
    const apiErr = new ApiError(err.code, err.message, res.status, { ...err, retryAfterSeconds });
    // Preserve the full parsed body: some endpoints put fields OUTSIDE `error`
    // (e.g. /api/docs/content 403 → { error, exists, title } for the B17
    // "exists but forbidden" presentation). Callers read err.body.<field>.
    apiErr.body = data;
    throw apiErr;
  }

  return data;
}

// Build a query string from a params object, skipping null/undefined/'' values.
function qs(params) {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params || {})) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export const api = {
  get: (path) => request('GET', path),
  post: (path, body) => request('POST', path, body),

  // ── SB-006 auth endpoints ────────────────────────────────────────────────
  me: () => request('GET', '/api/me'),
  requestCode: (email) => request('POST', '/api/auth/request-code', { email }),
  verifyCode: (email, code, tz) => request('POST', '/api/auth/verify-code', { email, code, tz }),
  loginPassword: (email, password) =>
    request('POST', '/api/auth/login-password', { email, password }),
  setPassword: (password) => request('POST', '/api/auth/set-password', { password }),
  // 第八十七批：提醒信里那枚次抛登录链接。**必须是 POST**——邮关与预取器会先
  // GET 一遍信里的每条链接，若 GET 即消费，令牌会在收信人点开前就被烧掉。
  loginLink: (token) => request('POST', '/api/auth/login-link', { token }),
  logout: () => request('POST', '/api/auth/logout'),

  // ── D7 self-service profile (SB-031): set own display name (nickname). ─────
  setProfile: (displayName) => request('POST', '/api/me/profile', { displayName }),

  // ── C6 admin account management (SB-031; is_admin only). ──────────────────
  adminJobPosts: () => request('GET', '/api/admin/job-posts'),
  adminListAccounts: () => request('GET', '/api/admin/accounts'),
  adminCreateAccount: (payload) => request('POST', '/api/admin/accounts', payload),
  adminUpdateAccount: (id, patch) => request('POST', `/api/admin/accounts/${id}`, patch),
  adminResetPassword: (id) => request('POST', `/api/admin/accounts/${id}/reset-password`),
  // ── 第一百零二批（多项目制 Slice B）：成员×组编辑 ─────────────────────────
  // **替换语义**：传进去的这一组就是该账号往后的全部成员关系，服务端不做并集。
  // 因此调用侧必须先把当前集合读出来（/api/admin/accounts 的行里带 projectGroupIds）
  // 再整份回传——只发"新勾的那个"会把其余的组一并抹掉。空数组是合法输入
  // （一个离组的账号就是零成员），所以这里不给它加任何"至少一个"的前端拦截：
  // 那会让"把某人移出所有组"在页面上无解，而后端明确接受它。
  adminSetAccountGroups: (id, projectGroupIds) =>
    request('POST', `/api/admin/accounts/${id}/groups`, { projectGroupIds }),
  // SB-037/F4: release valve for the per-email daily send cap — anyone who knows
  // a member's address can burn it, so an admin must be able to clear it.
  adminClearCodeQuota: (id) => request('POST', `/api/admin/accounts/${id}/clear-code-quota`),

  // ── SB-058/SB-057 external-collaborator git credentials (admin surface). ──
  // The issue response carries the token EXACTLY ONCE (hash-only storage).
  // 凭据体系改造（内外双线裁定批）后这三只是**兼容面**：后端保留旧端点，但页面
  // 已改走下面的凭据/授权两级新面——留着它们是给还没迁完的脚本调用方留路，不是
  // 给新代码用的。
  adminGitAccessList: () => request('GET', '/api/admin/git-access'),
  adminGitAccessIssue: (payload) => request('POST', '/api/admin/git-access', payload),
  adminGitAccessRevoke: (username) =>
    request('POST', `/api/admin/git-access/${encodeURIComponent(username)}/revoke`),

  // ── git 凭据体系改造（内外双线裁定批）：凭据/授权两级的新管理面 ────────────
  // 一枚凭据（username+token，明文只在签发响应里出现一次）下挂多条 grant
  // （仓×读写，吊销留行翻位）。仓名不再是自由文本：/api/admin/git-repos 是唯一
  // 数据源（服务端已滤敏感仓），页面上没有手输仓名的通道——写错仓名的凭据在
  // 默认拒绝的闸上表现为"好像发成功了但推不上去"，那种错要在下拉里就不可能发生。
  adminGitRepos: () => request('GET', '/api/admin/git-repos'),
  adminGitCredentials: () => request('GET', '/api/admin/git-credentials'),
  // 签发响应两态与旧面同宗（token 明文一次 / delivery 密件二选一，键整个不出现
  // 而不是空串——gitAccess.mjs 的 gitIssueResultKind 咬着这条契约）。
  adminGitCredentialIssue: (payload) => request('POST', '/api/admin/git-credentials', payload),
  adminGitGrantAdd: (credId, payload) =>
    request('POST', `/api/admin/git-credentials/${encodeURIComponent(credId)}/grants`, payload),
  adminGitGrantRevoke: (credId, grantId) =>
    request('POST', `/api/admin/git-credentials/${encodeURIComponent(credId)}/grants/${encodeURIComponent(grantId)}/revoke`, {}),
  // 写位翻转送**目标值**不送 toggle：两个管理员同时点，toggle 会翻两次落回原位，
  // 而各自送目标值最多是同一句话说两遍（幂等）。
  adminGitGrantWrite: (credId, grantId, canWrite) =>
    request('POST', `/api/admin/git-credentials/${encodeURIComponent(credId)}/grants/${encodeURIComponent(grantId)}/write`, { canWrite: Boolean(canWrite) }),
  adminGitCredentialRevoke: (credId) =>
    request('POST', `/api/admin/git-credentials/${encodeURIComponent(credId)}/revoke`, {}),
  // 第一百七十七批（SB-180 (d)）：给既存凭据绑定归属账号——绑定位是密送推导与
  // 介绍信的收件人来源，迁移搬来的旧行没有它就只剩吊销重签一条路。
  adminGitCredentialBind: (credId, accountId) =>
    request('POST', `/api/admin/git-credentials/${encodeURIComponent(credId)}/bind`,
      { accountId: Number(accountId) }),

  // ── 第八十批：用户 API key（会话等价凭据，仅管理员代签）──────────────────
  // 签发响应里的 `key` 是**明文**，库里只有它的 sha256——页面关掉就再也拿不到。
  // 所以这三个方法的调用点必须把明文一路交给"只显示一次"的那个提示框，
  // 绝不写进 localStorage / 状态持久层（那等于把它又存了一份，而且存在最容易
  // 被别的脚本读到的地方）。
  adminApiKeyList: (accountId) =>
    request('GET', `/api/admin/accounts/${encodeURIComponent(accountId)}/api-keys`),
  adminApiKeyIssue: (accountId, payload) =>
    request('POST', `/api/admin/accounts/${encodeURIComponent(accountId)}/api-keys`, payload),
  adminApiKeyRevoke: (keyId) =>
    request('DELETE', `/api/admin/api-keys/${encodeURIComponent(keyId)}`),

  // ── SB-148 B/C：系统站内信（仅管理员 + **仅经模板**）────────────────────────
  // body = { toAccount, template, context }。template 是服务端登记过的模板名
  // （第一批只有 'cli-onboarding'），正文由服务端渲染（zh/en 双语同体）——这里
  // **没有**自由正文参数，也永远不该有：绕排队的资格来自「内容是审过的模板」，
  // 不是「发信人是管理员」。context 只带提示位（如 keyLabel），绝不含 key 明文
  // ——key 走密件，信里只说去哪取。
  adminSendTemplateMessage: (payload) =>
    request('POST', '/api/admin/messages/template', payload),

  // ── SB-068/SB-077/SB-084 服务设置：每步骤的 LLM 模型（admin 面唯一入口）──
  // 后端从第六十三批起就有这两只端点了，**缺的一直只是页面**（第六十八批勘察实测：
  // 全仓没有任何 .jsx/.mjs 提过 admin/settings）。于是改模型这件事在页面上
  // 不存在，而它恰恰是那种想改的时候必须马上能改的东西。
  adminSettings: () => request('GET', '/api/admin/settings'),
  // SB-189 片 1 ③（第二百四十批）：LLM 已计价花费。不给窗口 ⇒ 服务端取最近 30 天。
  adminLlmSpend: (fromMs, toMs) => request('GET', `/api/admin/llm/spend${
    fromMs != null && toMs != null ? `?fromMs=${Number(fromMs)}&toMs=${Number(toMs)}` : ''}`),

  // ── 第八十一批 运行日志（应用层可观测）────────────────────────────────────
  // 后端的能力自 WI-110 就齐了（RuntimeLog 的 Query + list_entries + list_restarts），
  // 网关直到本批才有 logs 路由——在此之前"今天出了什么事"这个问题在界面上无解。
  // 参数与 RuntimeLog::Query 一一对位；**空值一律不发**（`category=` 在后端是
  // "精确匹配空类别"，能把全部行筛掉），拼装在 pages/adminLogs.mjs 的 buildLogQuery。
  adminLogs: (params = {}) => request('GET', `/api/admin/logs${qs(params)}`),
  // 重启标记。它不受写入级别约束（level=none 也照写），是"这条报错是这次启动
  // 之后的吗"唯一能回答的东西——而那是看日志时问的第一个问题。
  adminLogRestarts: (params = {}) => request('GET', `/api/admin/logs/restarts${qs(params)}`),
  // ── 第一百零九批 P1 ②：被**交出**的站内信（举报窄通道的管理面）─────────────
  // ⚠ 这是管理员在站内信上唯一的读法，而它只端得出被收件人**主动交出**的那几封
  //（后端 `list_reported` 的 WHERE 里写死 `reported_at_ms IS NOT NULL`）。
  // 管理员对其余任何一封仍然是 404——契约原裁「管理员读不到别人的信」原样保留，
  // 本端点是它旁边那条由收件人自己按下的窄缝，不是它的替代品。
  // 它落在运行日志页而不是自成一页，是因为这两件事是同一件事的两半：出事那天，
  // 一个人来这里问"今天出了什么事"，而"有人把一封信交上来了"正是那个答案的一部分。
  adminReportedMessages: (params = {}) =>
    request('GET', `/api/admin/reported-messages${qs(params)}`),
  // model 传空串 = **撤销覆盖**（回到部署值），不是设成空模型。
  adminSetLlmOperationModel: (op, model) =>
    request('POST', '/api/admin/settings/llm-operation', { op, model }),

  // ── 第一百一十批 A/B④：agent 逃生口（「我做不了」）的管理面 ─────────────────
  // 需求方点名要的那一面。在它之前，agent 说不出"我做不了"——最接近的
  // `recommendation:"need_ruling"` 落在**成功路径**上（它说的是"这件事该人裁"，
  // 不是"我做不了这个任务"），而真正做不成的三种情形（失败三次 / 额度耗尽 /
  // 该类型不预读）在 `agentReview: null` 上完全同形。
  //
  // 参数：{ acked: 'false'|'true'|'all', op, reason, limit, offset }
  // —— `acked` 刻意是**三值字符串**不是布尔：'false'（默认，只列未处置）与不传是两回
  // 事，前者是调用侧的明确表态。拼装在 pages/adminEscalations.mjs 的 escalationQuery。
  adminAgentEscalations: (params = {}) =>
    request('GET', `/api/admin/agent-escalations${qs(params)}`),
  // ── SB-116（第一百二十一批）：敏感读面主体审计——「某资源的读取记录」──────
  // 审计存了但查不到等于没存（工单原话）。resource **必填**（后端 400）：
  // 这条通道是按需查证，不是浏览器——不带资源标识的"全量翻审计"是 §2 明拒的
  // 另一种读面。fromMs/toMs 可选时间窗；拼装在 pages/readAudit.mjs 的
  // buildReadAuditQuery（空值一律不发，与 buildLogQuery 同一条纪律）。
  adminReadAudit: (params = {}) =>
    request('GET', `/api/admin/read-audit${qs(params)}`),
  // 处置。`note` **必填且 trim 后非空**（服务端 400）：一次没有说法的处置，与
  // 「没人管」在台账上无法区分。单向——没有 unack，也永远不该有：一个能收回的处置
  // 是一个可反复试探的开关（同 direct_message.reported_at_ms 那条纪律）。
  adminAckAgentEscalation: (id, note) =>
    request('POST', `/api/admin/agent-escalations/${encodeURIComponent(id)}/ack`, { note }),

  // SB-190 §7.1（第二百四十六批）：响应时限读面与裁决。管理页（步 5）与 endpoint-callers 都读这三行。
  // 窗口只收毫秒（fromMs/toMs）；responder 是逗号分隔的账号 id 串（缺席 = 全部响应方）。
  adminResponseSla: (params = {}) => request('GET', `/api/admin/response-sla${qs(params)}`),
  adminAddResponseSlaRuling: (payload) => request('POST', '/api/admin/response-sla/rulings', payload),

  // ── 第一百一十批 B②：把一条终局作业按原参数重排 ────────────────────────────
  // **通用解**：它不认识 `review_ticket`，也不认识任何一种 kind，只做"按原参数排一条
  // 新键（原键 + `:m<now_ms>`）的行"。于是「dedupe_key 永久占位」这个**类**的问题从此
  // 有一个总出口——存量那些被占位的键（dev 上 MNT-000012 的 113 号行）管理员在页面上
  // 按一下即可，无需改库、无需删行。
  // `force:true` 只在 `result_class==='permanent'` 时需要：那是"重试必然徒劳"
  // （400/401/403/404/413/422），不加 force 服务端回 409 RETRY_FUTILE。
  adminRequeueJob: (id, force) =>
    request('POST', `/api/admin/jobs/${encodeURIComponent(id)}/requeue`,
      { force: Boolean(force) }),

  // ── 第五十五批 项目组与文档改动追踪开关（契约 §4.4；admin 面唯一入口） ─────
  // 开关只在库里、只在这个面上；config 里**不设**——config 烤进镜像，重新部署会
  // 用文件值覆盖回去，把一个不可逆的闸静默解锁。
  adminProjectGroups: () => request('GET', '/api/admin/project-groups'),
  // SB-202 S3（第三百零八批，需求方指令「加项目层设置页」）：资方进度报告设置。只改给了的字段。
  adminSetReportSettings: (id, body) =>
    request('PUT', `/api/admin/project-groups/${id}/report-settings`, body),
  // 单向闸：只接受 enabled:true。传 false 由后端 400 IRREVERSIBLE 拒绝，故这里
  // 连参数都不给——前端根本不该有表达"关闭"的能力。
  adminEnableLogTracking: (id) =>
    request('POST', `/api/admin/project-groups/${id}/log-tracking`, { enabled: true }),

  // ── 第八十五批 运行期建组（需求方裁「项目组应该可以在运行期创建」）─────────
  // 中英两名**分别**传：只传一个再由后端复制到另一列，就是把中文名钉进英文列，
  // 而目录页照列名渲染（第四十三批语言规则）。后端对缺项回 400。
  // 201 → { id, slug, displayName, displayNameEn, …, frictionBacklogNo }
  // ——`frictionBacklogNo` 是同批建出的内置摩擦单（R-29 的归处），页面要显示它：
  // 它是"这个组已经完整可用"的凭据，而不是一个内部细节。
  adminCreateProjectGroup: ({ slug, displayName, displayNameEn }) =>
    request('POST', '/api/admin/project-groups', { slug, displayName, displayNameEn }),

  // ── 第八十六批 管理员浏览源码仓（只读；需求方裁"粗略查看"）─────────────────
  // 三条收窄：只到分支（不碰提交历史）、文本直展、二进制只给下载。
  // "是不是文本"由**后端**判（白名单在 gitea_client），前端不重判——重判就是把
  // 同一条规则放到两处，而两处迟早会漂开。
  adminRepos: () => request('GET', '/api/admin/repos'),
  adminRepoBranches: (repo) => request('GET', `/api/admin/repos/branches${qs({ repo })}`),
  adminRepoContents: ({ repo, path, ref }) =>
    request('GET', `/api/admin/repos/contents${qs({ repo, path, ref })}`),

  // 二进制下载地址（第一百一十八批）。**返回地址而不是发请求**：浏览器要的是一个
  // 能放进 href 的东西，由它自己去取（大文件不该先进 JS 的内存）。
  //
  // 为什么由前端拼、而不是让服务端把地址塞进 contents 的应答里：
  //   · 服务端此前塞的是 **gitea 按自己 ROOT_URL 拼的绝对地址**
  //     （`http://127.0.0.1:<git 口>/…`），浏览器点了就打不开——那是本批修的病；
  //   · 而把地址改成我方路由之后若仍由服务端塞，**这条端点在静态上就没有调用方**，
  //     `endpoint-callers` 会把它报成孤儿——那条判据是对的：一个没人调的端点
  //     不产生任何错误日志，只表现为"好像没人用"。
  // 所以产地放这里：一处真源，且判据看得见。
  adminRepoRawUrl: ({ repo, path, ref }) =>
    `/api/admin/repos/raw${qs({ repo, path, ref })}`,

  // ── 第八十五批 工时两参数（裁决：无理由绑定部署的配置走管理端点）───────────
  // 这两个值**不在** stage 配置里：配置烤进镜像，改一个业务参数就要走一次
  // 构建+部署，且下次部署把这里的改动覆盖回文件值。
  adminWorkHoursSettings: () => request('GET', '/api/admin/work-hours-settings'),
  // 只传改动过的键（见 groupCreate.mjs 的 workHoursFormState）：一次什么都不改的
  // POST 会在审计里留下一条"配置变更"，而那条记录说的不是真的。
  adminSetWorkHoursSettings: (payload) =>
    request('POST', '/api/admin/work-hours-settings', payload),
  // 第九十九批（SB-104）：燃耗对账。fromDay/toDay 必传——区间锚定查看者的日历，
  // 由 adminBurn.mjs 的 burnRange 算，服务端刻意不做默认（routes_admin 头注）。
  adminWorkLogBurn: ({ fromDay, toDay }) =>
    request('GET', `/api/admin/work-log/burn${qs({ fromDay, toDay })}`),

  // ── SB-017 M2 doc-serve endpoints ────────────────────────────────────────
  // Authorized doc tree for a group in a language (B13/B17/B10 applied server-side).
  docsTree: (group, lang) => request('GET', `/api/docs/tree${qs({ group, lang })}`),
  // Single doc view. 200 → { docKey, lang, title, html, availableLangs };
  // 403 → ApiError(status 403) with .body { error, exists, title } (B17: exists,
  // no permission); 404 → ApiError(status 404) (absent, concealed, or that
  // language not published).
  docContent: (group, docKey, lang) =>
    request('GET', `/api/docs/content${qs({ group, docKey, lang })}`),
  // Absolute URL for a referenced binary asset (authorized server-side). Used
  // for <img src>; not fetched through the JSON wrapper.
  docAssetUrl: (group, path) => `/api/docs/asset${qs({ group, path })}`,

  // ── 第一百零三批 可见职位（audience）─────────────────────────────────────
  // path 既可是 docKey 也可是文件夹前缀。200 → { path, audience:[{post, postEn,
  // via:"doc"|"subtree"}…] }（判定与真实鉴权同一只匹配器；名单只列 job posts，
  // 管理员不造伪条目）。调用者自己看不见的 path 一律 404 隐匿——所以它只该在
  // 文档已成功打开后才被调用，且失败静默省略：它是辅助信息，不该把文档页打红。
  docAudience: (group, path) => request('GET', `/api/docs/audience${qs({ group, path })}`),

  // ── 第五十五批 文档改动追踪（列表/明细分离，见契约 §4.2/§4.3） ────────────
  // 列表不含 diff 正文（大文本只在点开某一条时单取），倒序，每页 20。
  // 开关关闭时后端回 200 { enabled:false, entries:[], total:0 } —— **不是 404**，
  // 前端据此隐藏入口，而不是把"关闭"错当成"出错"。
  docHistory: (group, docKey, opts = {}) =>
    request('GET', `/api/docs/history${qs({
      group, docKey, limit: opts.limit, offset: opts.offset,
    })}`),
  // 单条改动明细（含 diff 正文）。开关关闭 ⇒ 404。
  docChange: (group, docKey, changeId) =>
    request('GET', `/api/docs/change${qs({ group, docKey, changeId })}`),

  // ── SB-025 (reactivated): ticket attachments (submitter-only) ────────────
  // Multipart upload — bypasses the JSON `request` wrapper deliberately.
  // SB-073（第六十二批）：`note` 与字节走**同一个请求**。此前它是上传之后另生成的一段
  // 可复制文本，而工单正文没有编辑通道——于是 67 件附件里 40 张照片没有任何地面真值。
  // 两个独立操作可以各成功一半，而"成功了一半的备注"就是今天这个局面。
  uploadTicketAttachment: async (no, file, note) => {
    const form = new FormData();
    form.append('file', file, file.name);
    if (note && String(note).trim()) form.append('note', String(note).trim());
    const res = await fetch(`/api/me/tickets/${no}/attachments`, {
      method: 'POST', credentials: 'include', body: form,
    });
    let body = null;
    try { body = await res.json(); } catch { /* non-JSON error body */ }
    if (!res.ok) {
      const err = body?.error ?? {};
      throw new ApiError(err.code, err.message, res.status, err);
    }
    return body;
  },
  // ── SB-067 一期（第六十四批）：AI 润色 ────────────────────────────────────
  // 后端把守门做成 fail-closed：润色稿丢了原文的编号/路径/URL 就整次作废（422），
  // 且**不扣额**。前端因此不需要自己校验，只需要把 422 的理由如实显示出来。
  polish: (text, scene) => request('POST', '/api/me/llm/polish', { text, scene }),
  polishQuota: () => request('GET', '/api/me/llm/quota'),

  listTicketAttachments: (no) => request('GET', `/api/me/tickets/${no}/attachments`),
  ticketAttachmentUrl: (no, id) => `/api/me/tickets/${no}/attachments/${id}`,

  // ── SB-064 interim slice (batch 50): the approver read-only surface ──────
  // SB-159（第三百八十二批）：`after` = 上一页的 nextCursor（键集游标）。应答带
  // total／limit／hasMore／nextCursor——审批队列靠 api/paging.mjs 的 fetchAllPages 一页页取到底。
  approverTickets: (opts = {}) =>
    request(
      'GET',
      `/api/approver/tickets${qs({
        status: opts.status,
        type: opts.type,
        archived: opts.archived,
        limit: opts.limit,
        after: opts.after,
        offset: opts.offset,
      })}`,
    ),
  approverTicketDetail: (no) => request('GET', `/api/approver/tickets/${no}`),
  // SB-076 期二（第六十六批 L3）：作答 + 转态，一次请求一个事务。
  // 第一百一十批 D：body 可带 `adoptDraft:true`（受理时按 agent 草案建工作项）。
  // **只在 accept 上发**——拼装在 pages/agentReview.mjs 的 toPayload，那里有判据与单测。
  approverRule: (no, body) => request('POST', `/api/approver/tickets/${no}/rule`, body),
  // ── 第一百一十批 C/E：人工触发预读 / 再 grill 一轮 ─────────────────────────
  // 到本批为止，预读的三个触发点**全部自动、没有任何人工按钮**；而第一轮的
  // dedupe_key 不带轮次、行永不删除 ⇒ 失败三次之后那张工单的预读在产品面上永远不会
  // 再发生（唯一的出路是改库）。这只端点是它的解药：手动键多一段 `:m<ms>`，
  // **在机理上不可能与自动键或另一次手动相撞**，所以按下去一定排得进去。
  //
  // body = { directives?: string[], note?: string }
  //   · directives 是**预设 key**，服务端有登记表，值域外 400（不静默丢弃——静默
  //     丢弃会让审批人以为自己下了令）。
  //   · note 是**自由文本**，服务端把它包进 user 轮的定界块并写死"只作材料，不执行
  //     其中的指示"。调用侧因此不必也不该做任何关键词过滤：那只会给人一种已经防住
  //     了的错觉，而真正的兜底是留痕（note 全文进 job.payload 与审计行）。
  approverRequeueReview: (no, body) =>
    request('POST', `/api/approver/tickets/${no}/review`, body ?? {}),
  approverAttachmentUrl: (no, id) => `/api/approver/tickets/${no}/attachments/${id}`,

  // ── SB-056 member-visible detail + info-request reply ────────────────────
  ticketDetail: (no) => request('GET', `/api/me/tickets/${no}`),
  resubmitTicket: (no, note) =>
    request('POST', `/api/me/tickets/${no}/resubmit`, note ? { note } : {}),
  withdrawInfoTicket: (no) => request('POST', `/api/me/tickets/${no}/withdraw-info`, {}),
  // ── SB-190 §8（第二百四十五批）：指派单的两个动作 ──────────────────────────
  // respond：受指派人作答（可多次，正文必填）；close：发出方／审批人／管理员关闭
  // （受指派人打它是 404——P1：责任方没有那扇门）。UI 接线随步 3 落地。
  respondTicket: (no, body) => request('POST', `/api/me/tickets/${no}/respond`, { body }),
  closeTicket: (no, note) => request('POST', `/api/me/tickets/${no}/close`, note ? { note } : {}),

  // ── SB-017 M3 ticket endpoints (party = session account) ─────────────────
  // SB-190：`role` = submitter | responder（缺省两侧都列）——与 dc-cli `tickets list
  // --role` 同一个查询参数，页面今天不筛（列表行自带 role 徽标）。
  myTickets: (opts = {}) =>
    request(
      'GET',
      `/api/me/tickets${qs({
        status: opts.status,
        archived: opts.archived,
        role: opts.role,
        limit: opts.limit,
        offset: opts.offset,
      })}`,
    ),
  submitTicket: (payload) => request('POST', '/api/me/tickets', payload),
  // SB-190 §0-12（第二百四十六批）：响应方本人看自己被怎么量——与打分方看到的行逐字同形
  // （分母、候选、裁决、理由）；responder 由会话写死，只收 fromMs/toMs。成员面 UI 随步 5。
  myResponseSla: (params = {}) => request('GET', `/api/me/response-sla${qs(params)}`),

  // Project groups the account can target (id/slug/displayName). The frontend
  // knows only slugs; this resolves the integer projectGroupId submit needs.
  projectGroups: () => request('GET', '/api/project-groups'),

  // SB-046: the ticket-type picker feed from the server-side registry.
  ticketTypes: () => request('GET', '/api/ticket-types'),

  // ── 第一百零二批（SB-106）：项目汇报读面（成员面，cookie 即可）─────────────
  // 汇报是**库里的实体**，不是 data_content 里的文档——所以它不经发布管线、不受
  // view glob 授权，可见性只有一条：调用者是不是这个组的成员（非成员一律 404
  // "不存在"，连"有这么个组"都不承认）。故这两只方法不带任何权限参数。
  // projectGroupId 必传：后端也收 slug，但页面手里拿得到的是会话上下文里的整数 id，
  // 用 slug 等于把一个已知量绕成一次字符串匹配。
  projectReports: (projectGroupId) =>
    request('GET', `/api/project-reports${qs({ projectGroupId })}`),
  // 单篇（含正文与最近修订）。reportKey 允许分段（`status/weekly`），故整段编码而
  // 不是 encodeURIComponent——后者会把段分隔的 '/' 编成 %2F，而路由是贪婪捕获的
  // 原始路径，%2F 会变成 key 的一部分，查无此篇。
  projectReport: (reportKey, projectGroupId) =>
    request('GET', `/api/project-reports/${String(reportKey).split('/').map(encodeURIComponent).join('/')}${qs({ projectGroupId })}`),

  // ── 第一百零四批 B（SB-106 系）：组间通信 ─────────────────────────────────
  // 读面**全员全量**（需求方裁的透明口径）：列表与详情都不带组参数，可见性不按
  // currentGroup 分片——这正是它与 projectReports 的分野。写面（发起/回帖）由
  // /api/me account 块的 canLiaison 显隐，真边界在后端（无权 403）。
  // 第一百二十五批 C：已归档的线**默认不在列表里**（裁决③），带参数才出现。
  // 过滤在服务端做：归档的意思就是"别再占着收件箱"，让每个调用方各自记得滤掉，
  // 等于把同一条裁决实现三遍（页面、CLI、peer 脚本）。
  groupThreads: ({ includeArchived } = {}) =>
    request('GET', `/api/group-threads${includeArchived ? '?includeArchived=1' : ''}`),
  groupThread: (id) => request('GET', `/api/group-threads/${encodeURIComponent(id)}`),
  // parties / asGroup / group 都是组 **slug**（合同：消息行的 asGroup 是 slug，
  // parties 数组同构）。主体是组不是人：authorDisplay 由服务端从会话取，这里不传。
  // 第一百零六批 C1：建线发 `parties`（≥2，**第 0 项是发起组**——后端拿它署开场白
  // 并判成员闸）。后端仍收 fromGroup/toGroup，但本前端一律走新字段：两种写法并存
  // 时页面只该有一种，否则"哪个字段生效"要靠读后端代码才知道。
  createGroupThread: ({ subject, parties, body }) =>
    request('POST', '/api/group-threads', { subject, parties, body }),
  replyGroupThread: (id, { body, asGroup }) =>
    request('POST', `/api/group-threads/${encodeURIComponent(id)}/messages`, { body, asGroup }),
  // 追加参与组。幂等：重复邀请回 200 且 added=false（不是错误）。
  addGroupThreadParty: (id, group) =>
    request('POST', `/api/group-threads/${encodeURIComponent(id)}/parties`, { group }),
  // ── 第一百二十五批 C：归档三件（需求方 2026-08-20 三裁）──────────────────────
  // 三条都**不发请求体**：动作本身就是全部信息。想说"为什么否决"就在线里说一句，
  // 而那句话本身就顺带取消了归档（裁决④），不必再有一个没人读的备注字段。
  // 三条都回**动作之后**的线（含 archiveState），调用方据它对账。
  requestGroupThreadArchive: (id) =>
    request('POST', `/api/group-threads/${encodeURIComponent(id)}/archive`, {}),
  cancelGroupThreadArchive: (id) =>
    request('POST', `/api/group-threads/${encodeURIComponent(id)}/archive/cancel`, {}),
  reopenGroupThread: (id) =>
    request('POST', `/api/group-threads/${encodeURIComponent(id)}/reopen`, {}),

  // ── 第一百零九批 D：一对一站内信 ──────────────────────────────────────────
  // 四只端点 + 一只锚点端点。三条纪律写在这里，因为它们是**调用侧**的事：
  //  ① 列表端点**永不含正文**（后端的行结构里就没有 body），所以调用方不该有任何
  //     "顺手预览一下"的写法——裁定 3 把"点开正文即已读"定成了判据，列表里露一个字，
  //     撤回边界与防 spam 判据同时失真。
  //  ② `message(id)` **有副作用**：收件人调它就是把这封信标成已读（对方收到回执、
  //     撤回窗口永久关死）。所以它只能被"用户明确点了打开"这一条路径调用，
  //     绝不能进 useLoad 的自动加载、绝不能被铃铛的跳转顺手触发。
  //  ③ 撤回是 DELETE 而不是 POST：它的语义就是"这封信从未到达"（正文一并清空）。
  // `before` = 上一页最后一行的 id（SB-159 游标分页）。响应带 hasMore / nextCursor：
  // **没有它们，"这一页 100 条"与"世界一共 100 条"不可区分**。
  messages: (opts = {}) =>
    request('GET', `/api/me/messages${qs({ box: opts.box, limit: opts.limit, before: opts.before })}`),
  // ⚠ 副作用见上 ②。函数名刻意叫 openMessage 而不是 getMessage：get 读起来像是免费的。
  openMessage: (id) => request('GET', `/api/me/messages/${encodeURIComponent(id)}`),
  // ── SB-146（第一百二十二批）：强读闸的两只端点 ────────────────────────────
  // peek=1 取正文**不标已读**（契约 §3.2）：强读闸要先把信端到人眼前、等他真读完
  // 才标——用 openMessage 取体等于「遮罩一弹出就已读」，闸就成了空转的仪式。
  // 不带 peek 的既有语义（打开即已读）一字不变，收件箱照旧走 openMessage。
  peekMessage: (id) => request('GET', `/api/me/messages/${encodeURIComponent(id)}?peek=1`),
  // 显式标已读（契约 §3.3；幂等）。只在强读闸「滚动到底 + 3 秒倒计时完成」那一刻
  // 调——排队机制由已读事件放行下一封，这只函数正是让它转起来的那只手。
  markMessageRead: (id) =>
    request('POST', `/api/me/messages/${encodeURIComponent(id)}/read`, {}),
  sendMessage: (payload) => request('POST', '/api/me/messages', payload),
  withdrawMessage: (id) => request('DELETE', `/api/me/messages/${encodeURIComponent(id)}`),
  // ④ **举报窄通道**（第一百零九批 P1 ②，需求方裁）：收件人把**这一封**交给管理员，
  //    交出之后那一封的正文对管理面开放。三条调用侧的纪律：
  //    · **不可撤销**——没有 unreportMessage，也永远不该有：一个能收回的"交出"是一个
  //      可反复试探的开关。所以这只函数**必须**由一个说清了这一点的确认对话触发，
  //      与 takeSecretDelivery 同一条纪律（那一只是"取走就没了"）。
  //    · 只有已读的信交得出去（后端 409 DM_NOT_READ）：已读的信撤不回，于是交出去的
  //      正文永远不会被清空。
  //    · **发送方不被通知**，所以调用它之后不必也不该做任何"通知对方"的动作。
  reportMessage: (id) =>
    request('POST', `/api/me/messages/${encodeURIComponent(id)}/report`),
  // 「我能拿什么锚点给这个人写信」。它同时带回 blockedByUnread / pendingMessageId，
  // 好让前端**在他打完 2000 字之前**就把"这封只能排队"或"你已经有一封在排"说出来。
  messageAnchors: (toAccount, opts = {}) =>
    request('GET', `/api/me/message-anchors${qs({
      toAccount, q: opts.q, docLimit: opts.docLimit,
    })}`),
  // 「我能给谁写信」（SB-169）。名单＝同组 ∪ 曾给我写过信者——两半各对应一族真能
  // 成立的锚点，所以下拉里的人选了就发得出去。**投影里没有邮箱**（OQ-C32 (b)）：
  // 提交值走账号 id，区分符走成员键。
  messageRecipients: () => request('GET', '/api/me/message-recipients'),

  // ── 第一百零九批 D：定向密递的**页面**取件面 ──────────────────────────────
  // 后端这三只从第一百零六批就在（全是 require_session，浏览器一直调得动），
  // 缺的一直只是页面——而 CLI 需要一枚 key 才能跑 ⇒「用 key 才能取到 key」是死锁。
  // ⚠ `takeSecretDelivery` 是全站唯一一处**破坏性的读**：一件 once 密件调一次就没了。
  // 因此它绝不能出现在任何自动加载路径里（进页面只列清单；按一次 F5 就会烧掉一件），
  // 而且必须由一个说清了"取走就没了"的确认对话触发。返回体里的 `body` 只进
  // readonly 输入框，绝不进 URL / localStorage / console。
  // SB-159（第一百七十八批）：键集分页——before = 上一页最后一行 id。
  secretDeliveries: (opts = {}) =>
    request('GET', `/api/secret-deliveries${qs({ limit: opts.limit, before: opts.before })}`),
  takeSecretDelivery: (id) => request('GET', `/api/secret-deliveries/${encodeURIComponent(id)}`),
  revokeSecretDelivery: (id) =>
    request('DELETE', `/api/secret-deliveries/${encodeURIComponent(id)}`),

  // ── 第一百零九批 D：CLI 分发清单（下载页的元信息）─────────────────────────
  // 整包**不走这里**：它是 `<a href="/api/cli/bundle">` 直链（同源 + cookie 会话），
  // 理由见 CliDownloadPage 头注。这一只只取 {version, files, bundle:{bytes,sha256}}。
  cliManifest: () => request('GET', '/api/cli/manifest'),

  // ── SB-052 phase 1: in-app notifications ─────────────────────────────────
  // SB-159（第三百八十二批）：`before` = 上一页的 nextCursor（键集游标，新行插在头部）。应答带
  // limit／hasMore／nextCursor；铃铛的「加载更多」只在 hasMore === true 时出现。
  notifications: (opts = {}) =>
    request('GET', `/api/me/notifications${qs({
      unread: opts.unread ? 'true' : undefined,
      limit: opts.limit,
      before: opts.before,
    })}`),
  markNotificationRead: (id) => request('POST', `/api/me/notifications/${id}/read`, {}),
  markAllNotificationsRead: () => request('POST', '/api/me/notifications/read-all', {}),

  // ── SB-117（第一百一十三批）到期动作 ────────────────────────────────────
  // 三条端点凭据分三档；浏览器这一侧只用得到 list 与 settle（建行走 CLI，
  // 因为立行的时机是"写裁决记录的那个回合"，而那个回合发生在工作空间里）。
  // ── SB-197 S3a/S3b（第二百九十一、九十二批）：资产对账表四态记录 ─────────────
  // 成员面两条：读＝能看台账页的岗位；写＝岗位派生角色（服务端判，页面只按 `role` 显隐）。
  // `rows: true` 带回镜像行（台账主表的机读投影），页面据此建巢状树、不再解析 md。
  assetStates: (opts = {}) =>
    request('GET', `/api/asset-states${qs({
      group: opts.group, ma: opts.ma, since: opts.since, rows: opts.rows ? 1 : undefined,
    })}`),
  recordAssetState: (body) => request('POST', '/api/asset-states', body),
  // ── SB-201 S2/S3（第三百零三、三百零八批）：功能代码妥善率表三态记录 ─────────────
  // 与上一组同形：读＝能看功能表页的岗位；写＝岗位派生角色（program／qa／admin），页面只按 `role` 显隐。
  featureStates: (opts = {}) =>
    request('GET', `/api/feature-states${qs({
      group: opts.group, fp: opts.fp, since: opts.since, rows: opts.rows ? 1 : undefined,
    })}`),
  recordFeatureState: (body) => request('POST', '/api/feature-states', body),

  dueActions: (opts = {}) =>
    request('GET', `/api/due-actions${qs({
      includeSettled: opts.includeSettled ? 1 : undefined,
    })}`),
  settleDueAction: (id, state, note) =>
    request('POST', `/api/due-actions/${id}/settle`, { state, note: note ?? '' }),

  // ── SB-034 work hours (member surface; own data only) ─────────────────────
  // Backlogs that may still receive hours (R-04 pending|in_progress, non-friction)
  // — the picker source, so a selection can never be rejected by those rules.
  // SB-056: rows now carry body + isStakeholder; { stakeholder: true } narrows
  // to the caller's own assignments (the asset form's related-work-item picker).
  // OQ-A19 (batch 43): `lang` resolves title/body server-side (translation row
  // or the ruled fallback-to-original; rows carry sourceLang/resolvedLang).
  // SB-159（第三百八十二批）：`limit` 转交（服务端钳 500、缺省 200），应答带 hasMore——三个页面都要它。
  myBacklogs: (projectGroupId, opts = {}) =>
    request('GET', `/api/me/backlogs${qs({
      projectGroupId,
      stakeholder: opts.stakeholder ? 'true' : undefined,
      lang: opts.lang,
      limit: opts.limit,
    })}`),
  // Own rows in a YYYYMMDD day range → { rows, tz, today, windowDays, mayLog }.
  myWorkLog: (fromDay, toDay) => request('GET', `/api/me/work-log${qs({ fromDay, toDay })}`),
  // Entry. The timezone is taken from the account server-side — never sent.
  logWork: (payload) => request('POST', '/api/me/work-log', payload),
  // R-36 correction: an invested row can only be adjusted by appending a reversal.
  reverseWorkLog: (id, minutes, reason) =>
    request('POST', `/api/me/work-log/${id}/reverse`, { minutes, reason }),
  // R-08 (batch 84): a PLANNED row may be edited or deleted by its owner before
  // the day arrives — the other half of R-36's division of labour (invested rows
  // are never edited in place; planned rows never go through the reversal
  // channel). The server implements both as replace-by-append (the original goes
  // to `released` with an audit row; nothing is ever physically deleted, R-30),
  // so DELETE here means "withdraw this plan", not "erase the record".
  // `patch` = { workDay?, minutes? } — only the fields that actually changed.
  editPlannedWorkLog: (id, patch) => request('PATCH', `/api/me/work-log/${id}`, patch),
  deletePlannedWorkLog: (id) => request('DELETE', `/api/me/work-log/${id}`),
};

export default api;
